import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { ShareView } from '@photos/shared';
import { ApiError, api } from '../api/client';
import { keys } from '../api/hooks';
import { Hero } from '../components/Hero';
import { Icon } from '../components/Icon';
import { useEscape } from '../hooks/useEscape';
import { chooseSaveTarget, streamZip, triggerUrlDownload } from '../lib/download';
import { fmtCount, fmtRangeIso, fmtShortDate, fmtSize } from '../lib/format';
import { justify, targetRowHeight } from '../lib/justify';
import { thumbhashDataUrl } from '../lib/thumbhash';
import { useToast } from '../state/toasts';

type SharedPhoto = ShareView['photos'][number];

function Viewer({ photos, index, onIndex, onClose }: { photos: SharedPhoto[]; index: number; onIndex(i: number): void; onClose(): void }) {
  const p = photos[index]!;
  const ar = p.width / p.height;
  const ph = thumbhashDataUrl(p.thumbhash);
  useEscape(onClose);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
      if (e.key === 'ArrowRight' && index < photos.length - 1) onIndex(index + 1);
    };
    window.addEventListener('keydown', h);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', h);
      document.body.style.overflow = prev;
    };
  }, [index, photos.length, onIndex]);
  return createPortal(
    <div className="lb open" role="dialog" aria-modal="true" aria-label={p.fileName} style={{ gridTemplateColumns: '1fr', gridTemplateRows: '1fr' }}>
      <div className="stage">
        <button type="button" className="x" onClick={onClose}>
          <Icon name="x" /> Close
        </button>
        <span className="pos">
          {index + 1} / {photos.length}
        </span>
        <button type="button" className="nav prev" aria-label="Previous" disabled={index === 0} onClick={() => onIndex(index - 1)}>
          <Icon name="left" />
        </button>
        <img
          key={p.id}
          alt={p.fileName}
          src={p.previewUrl}
          style={{
            aspectRatio: String(ar),
            width: `min(100%, calc((100vh - 112px) * ${ar}))`,
            ...(ph ? { backgroundImage: `url(${ph})`, backgroundSize: 'cover' } : {}),
          }}
        />
        <button
          type="button"
          className="nav next"
          aria-label="Next"
          disabled={index >= photos.length - 1}
          onClick={() => onIndex(index + 1)}
        >
          <Icon name="right" />
        </button>
        {p.downloadUrl && (
          <button
            type="button"
            className="btn sm"
            style={{ position: 'absolute', top: 10, right: 14 }}
            onClick={() => triggerUrlDownload(p.downloadUrl!, p.fileName)}
          >
            <Icon name="dl" />
            Download
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Public share link: a justified grid with downloads as the link allows. No account needed. */
export function SharePage() {
  const { shareId = '' } = useParams();
  const toast = useToast();
  const share = useQuery({
    queryKey: keys.share(shareId),
    queryFn: () => api<ShareView>(`/shares/${encodeURIComponent(shareId)}`, { auth: false }),
    retry: false,
  });
  const grid = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1000);
  const [lb, setLb] = useState(-1);

  useLayoutEffect(() => {
    const el = grid.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth || 1000);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [share.data]);

  const photos = useMemo(() => share.data?.photos ?? [], [share.data]);
  const rows = useMemo(
    () => justify(photos.map((p) => ({ p, ar: p.width / p.height })), width, targetRowHeight(window.innerWidth)),
    [photos, width],
  );

  const downloadAll = async () => {
    const files = photos.filter((p) => p.downloadUrl).map((p) => ({ name: p.fileName, url: p.downloadUrl!, bytes: 0 }));
    const target = await chooseSaveTarget(`JBCH-photos-${files.length}.zip`);
    if (!target) return;
    const t = toast({ icon: 'dl', title: `Zipping ${files.length} photos`, sub: 'Streaming in your browser…', progress: 0 });
    try {
      await streamZip(files, target, (loaded, file) =>
        t.update({ sub: `${file} of ${files.length} · ${fmtSize(loaded)}`, progress: (file - 1) / files.length }),);
      t.update({ progress: 1, sub: `Saved ${target.name}` });
      t.close(3500);
    } catch (e) {
      t.update({ progress: undefined, title: 'Download failed', sub: (e as Error).message });
      t.close(6000);
    }
  };

  const err = share.error instanceof ApiError ? share.error : null;
  const first = photos.reduce<string | null>((a, p) => (!a || p.takenAt < a ? p.takenAt : a), null);
  const last = photos.reduce<string | null>((a, p) => (!a || p.takenAt > a ? p.takenAt : a), null);
  const span = fmtRangeIso(first, last);
  const sub = share.data
    ? [`${fmtCount(photos.length)} photos`, ...(span ? [span] : []), `Link expires ${fmtShortDate(new Date(share.data.expiresAt))}`]
    : [];

  return (
    <>
      <header className="top">
        <div className="top-row studio-row solo">
          <div className="top-left">
            <span className="brand">
              <img className="logo" alt="JBCH" src="/brand/jbch-emblem-180.png" />
              <b>Photos</b>
            </span>
          </div>
        </div>
      </header>
      <main className="wrap" style={{ maxWidth: 1400, margin: '0 auto' }}>
        {share.isPending ? (
          <div className="sentinel">
            <div className="spinner" />
          </div>
        ) : share.isError ? (
          <div className="empty">
            <h3>{err?.status === 410 ? 'This link has expired' : 'This link isn’t available'}</h3>
            {err?.status === 410
              ? 'Ask the person who shared it for a new link.'
              : 'It may have been revoked, or the address is incomplete.'}
          </div>
        ) : (
          <>
            <Hero
              title="Shared photos"
              sub={sub}
              coverUrl={photos[0]?.thumbUrl}
              layoutKey={sub.join('|')}
              actions={
                share.data!.allowDownload !== 'none' && photos.length > 0 ? (
                  <button type="button" className="btn sm" onClick={downloadAll}>
                    <Icon name="dl" />
                    Download all{share.data!.allowDownload === 'original' ? ' (originals)' : ' (large)'}
                  </button>
                ) : undefined
              }
            />
            <div id="grid" ref={grid}>
              {!photos.length ? (
                <div className="empty">
                  <h3>No photos</h3>The photos in this link are no longer available.
                </div>
              ) : (
                rows.map((r, i) => (
                  <div className="jrow" key={i}>
                    {r.tiles.map(({ item: { p }, width: w, height: h }) => {
                      const ph = thumbhashDataUrl(p.thumbhash);
                      return (
                        <div
                          key={p.id}
                          className="tile"
                          style={{ width: w, height: h }}
                          role="button"
                          tabIndex={0}
                          aria-label={p.fileName}
                          onClick={() => setLb(photos.indexOf(p))}
                          onKeyDown={(e) => e.key === 'Enter' && setLb(photos.indexOf(p))}
                        >
                          <img
                            loading="lazy"
                            decoding="async"
                            alt={p.fileName}
                            src={w > 500 || h > 500 ? p.previewUrl : p.thumbUrl}
                            style={ph ? { backgroundImage: `url(${ph})`, backgroundSize: 'cover' } : undefined}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </main>
      {lb >= 0 && photos[lb] && <Viewer photos={photos} index={lb} onIndex={setLb} onClose={() => setLb(-1)} />}
    </>
  );
}
